import type { SpinWheelVisualSegment } from './spinWheelVisual.types';

export function buildDismissKey(customerId: number | string | undefined): string {
  return `spin-wheel-dismissed:${String(customerId || 'guest')}`;
}

/**
 * Çark, backend'in etkin olarak döndürdüğü her segmenti birebir gösterir.
 *
 * Sabit dilim sayısı iki güven sorununa yol açıyordu: az segmentte aynı ödül
 * ekranda birden çok görünüyordu, 12'den fazla segmentte ise geçerli bir
 * backend sonucu çarkta hiç bulunmayabiliyordu. Görsel sıra artık backend
 * sırasıdır; seçilen sonuç bu listede tek, görünür bir hedefe sahiptir.
 */
export function buildVisualSlices<T extends SpinWheelVisualSegment>(segments: readonly T[]): T[] {
  return visualWheelSegments(segments);
}

/**
 * Sunucunun seçtiği segmentin çarktaki tek görünür konumunu bulur.
 *
 * Görsel dilimler backend sırasını koruduğu için bu indeks, animasyona
 * doğrudan verilebilir. Eski 12 dilim sınırı kaldırıldığı için 12'den sonraki
 * aktif segmentler de bu aramada yer alır.
 */
export function findVisualSliceIndex(
  slices: readonly SpinWheelVisualSegment[],
  segmentId: string
): number {
  return slices.findIndex((slice) => slice.id === segmentId);
}

const ROULETTE_DIVIDER_COLOR = 'rgba(28, 18, 10, 0.95)';

/** Çarkta gösterilecek dilimler: kayıt sırası, sadece aktif. Ağırlık görseli etkilemez. */
export function visualWheelSegments<T extends SpinWheelVisualSegment>(segments: readonly T[]): T[] {
  return segments.filter((s) => s.active);
}

function equalSliceGeometry(count: number): { sliceDeg: number; dividerDeg: number; colorSpanDeg: number } {
  if (count <= 0) {
    return { sliceDeg: 0, dividerDeg: 0, colorSpanDeg: 0 };
  }

  const sliceDeg = 360 / count;
  const dividerDeg =
    count >= 2 ? Math.min(0.85, Math.max(0.28, sliceDeg * 0.045)) : 0;
  const colorSpanDeg = sliceDeg - dividerDeg;

  return { sliceDeg, dividerDeg, colorSpanDeg };
}

/**
 * Eşit açılı rulet dilimleri (conic-gradient).
 * `SpinWheelCanvas` ile aynı dilim seti: her etkin backend segmenti, tekrarsız.
 */
export function buildWheelGradient(segments: readonly SpinWheelVisualSegment[]): string {
  const slices = buildVisualSlices(segments);

  if (slices.length === 0) {
    return 'conic-gradient(from -90deg, #1d2737 0deg 360deg)';
  }

  const { sliceDeg, colorSpanDeg } = equalSliceGeometry(slices.length);
  const parts: string[] = [];

  slices.forEach((segment, index) => {
    const sliceStart = index * sliceDeg;
    const colorEnd = sliceStart + colorSpanDeg;
    const sliceEnd = sliceStart + sliceDeg;

    parts.push(`${segment.color} ${sliceStart}deg ${colorEnd}deg`);
    if (slices.length >= 2) {
      parts.push(`${ROULETTE_DIVIDER_COLOR} ${colorEnd}deg ${sliceEnd}deg`);
    }
  });

  return `conic-gradient(from -90deg, ${parts.join(', ')})`;
}
